import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline as UIcon, List, ListOrdered, Quote, Link2, Heading2, Heading3, Undo2, Redo2 } from "lucide-react";

export default function RichTextEditor({ value = "", onChange, placeholder = "Type your notice…", minHeight = 220, testid = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-terracotta underline" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-stone max-w-none focus:outline-none",
        "data-testid": testid,
      },
    },
  }, [value === "" ? "empty" : "hasvalue"]);

  if (!editor) return null;

  const btn = "inline-flex items-center justify-center w-8 h-8 rounded-md text-stone-600 hover:bg-sand-100 hover:text-ink transition-colors";
  const active = "bg-sand-200 text-ink";
  const setLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("URL", previous || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rounded-xl border border-stone-300 bg-white focus-within:border-terracotta focus-within:ring-2 focus-within:ring-terracotta/20 overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-stone-200 bg-sand-50 px-2 py-1.5">
        <button type="button" aria-label="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${btn} ${editor.isActive("heading", { level: 2 }) ? active : ""}`}><Heading2 size={16}/></button>
        <button type="button" aria-label="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`${btn} ${editor.isActive("heading", { level: 3 }) ? active : ""}`}><Heading3 size={16}/></button>
        <span className="w-px h-5 bg-stone-300 mx-1"/>
        <button type="button" aria-label="Bold" onClick={() => editor.chain().focus().toggleBold().run()} className={`${btn} ${editor.isActive("bold") ? active : ""}`} data-testid="rte-bold"><Bold size={16}/></button>
        <button type="button" aria-label="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} className={`${btn} ${editor.isActive("italic") ? active : ""}`} data-testid="rte-italic"><Italic size={16}/></button>
        <button type="button" aria-label="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} className={`${btn} ${editor.isActive("underline") ? active : ""}`} data-testid="rte-underline"><UIcon size={16}/></button>
        <span className="w-px h-5 bg-stone-300 mx-1"/>
        <button type="button" aria-label="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${btn} ${editor.isActive("bulletList") ? active : ""}`} data-testid="rte-ul"><List size={16}/></button>
        <button type="button" aria-label="Ordered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${btn} ${editor.isActive("orderedList") ? active : ""}`} data-testid="rte-ol"><ListOrdered size={16}/></button>
        <button type="button" aria-label="Blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`${btn} ${editor.isActive("blockquote") ? active : ""}`}><Quote size={16}/></button>
        <button type="button" aria-label="Link" onClick={setLink} className={`${btn} ${editor.isActive("link") ? active : ""}`}><Link2 size={16}/></button>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" aria-label="Undo" onClick={() => editor.chain().focus().undo().run()} className={btn}><Undo2 size={16}/></button>
          <button type="button" aria-label="Redo" onClick={() => editor.chain().focus().redo().run()} className={btn}><Redo2 size={16}/></button>
        </div>
      </div>
      <div className="px-4 py-3" style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .ProseMirror { min-height: ${minHeight - 24}px; outline: none; }
        .ProseMirror p { margin: 0 0 0.6em 0; }
        .ProseMirror h2 { font-family: 'Fraunces', serif; font-size: 1.4rem; margin: 0.4em 0 0.4em 0; letter-spacing: -0.01em; }
        .ProseMirror h3 { font-family: 'Fraunces', serif; font-size: 1.15rem; margin: 0.4em 0 0.3em 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.3em; margin: 0.4em 0; }
        .ProseMirror li { margin: 0.15em 0; }
        .ProseMirror blockquote { border-left: 3px solid #1E3F63; padding-left: 0.9em; color: #57534E; margin: 0.5em 0; font-style: italic; }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder); float: left; color: #a8a29e; pointer-events: none; height: 0;
        }
      `}</style>
    </div>
  );
}
